import { motion } from 'framer-motion';
import type { ReactNode } from 'react';
import { Button } from '../../components';
import { cn } from '../../lib/cn';
import { springDefault, staggerSiblings } from '../../tokens';

type StepLayoutProps = {
  icon: ReactNode;
  title: string;
  description?: string;
  /** The interactive content for the step (input, picker, etc.). */
  children: ReactNode;
  /**
   * Override the default footer. Pass `null` to render no footer at all
   * (e.g. when the step's own content owns the Continue action — the
   * income step does this with NumPad's Done key).
   */
  footer?: ReactNode | null;
  /** Default footer config, used when `footer` isn't provided. */
  continueLabel?: string;
  onContinue?: () => void;
  canContinue?: boolean;
};

/**
 * Shared shell for every onboarding step. Owns the icon, headline,
 * subtext, content slot, and the bottom Continue button. Children fade
 * in with a stagger so the page feels alive when entering.
 */
export function StepLayout({
  icon,
  title,
  description,
  children,
  footer,
  continueLabel = 'Continue',
  onContinue,
  canContinue = true,
}: StepLayoutProps) {
  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto overscroll-contain">
        <motion.div
          initial="hidden"
          animate="show"
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: staggerSiblings } },
          }}
          className="flex flex-col px-screen-x pt-6 pb-4"
        >
          <motion.div
            variants={fadeUp}
            transition={springDefault}
            className={cn(
              'w-12 h-12 rounded-card bg-accent/10 inline-flex items-center justify-center',
              'text-accent mb-section-gap',
            )}
          >
            {icon}
          </motion.div>
          <motion.h1
            variants={fadeUp}
            transition={springDefault}
            className="text-[24px] font-semibold tracking-tight text-text-primary"
          >
            {title}
          </motion.h1>
          {description ? (
            <motion.p
              variants={fadeUp}
              transition={springDefault}
              className="mt-2 text-body-lg text-text-secondary"
            >
              {description}
            </motion.p>
          ) : null}
          <motion.div
            variants={fadeUp}
            transition={springDefault}
            className="mt-section-gap"
          >
            {children}
          </motion.div>
        </motion.div>
      </div>
      {footer === undefined ? (
        <div className="px-screen-x pt-4 pb-[max(env(safe-area-inset-bottom),20px)]">
          <Button
            variant="primary"
            size="lg"
            fullWidth
            disabled={!canContinue}
            onClick={onContinue}
          >
            {continueLabel}
          </Button>
        </div>
      ) : footer === null ? null : (
        <div className="px-screen-x pt-4 pb-[max(env(safe-area-inset-bottom),20px)]">
          {footer}
        </div>
      )}
    </div>
  );
}

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0 },
};
